import CreativeRecovery from './CreativeRecovery';
import CreativeAssignmentResults from './CreativeAssignmentResults';
import UniversalAdsCreativeAssets from './UniversalAdsCreativeAssets';
import { api } from '../api/apiConfig';
import CreativePackageReview, { missingCreativeCopy } from './CreativePackageReview';
import { creativeReadiness, metaTargetingReadiness } from '../utils/creativeReadiness';
import { initialCreativeMapping } from './CreativeConceptReview';
import { friendlyCampaignError } from '../utils/campaignErrors';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  campaignErrorMessage,
  draftAiCampaign,
  getAiCampaignDraft,
  getConnection,
  listAdAccounts,
  listFacebookPages,
  listGoogleAdsCustomers,
  publishAiCampaign,
  uploadCreativeZip,
} from '../api/adpilotApi';
import { channelFor } from '../channels';
import ChannelTabs, { readActiveChannel } from './ChannelTabs';
import CampaignStatusCard from './CampaignStatusCard';
import CampaignSetupFields, { CampaignSetupReview, DEFAULT_CAMPAIGN_SETUP, setupFromBrief, isAdpilotWorkflow } from './CampaignSetupFields';

const iso = (date) => date.toISOString().slice(0, 10);
const futureDate = (days) => {
  const value = new Date();
  value.setDate(value.getDate() + days);
  return iso(value);
};

const EMPTY_FORM = {
  name: '', startDate: futureDate(1), endDate: futureDate(30), budget: '1000',
  brandUrl: '', geo: '', goal: 'performance', additionalContext: '',
  campaignSetup: DEFAULT_CAMPAIGN_SETUP, creativeCopy: {}, creativeDatePolicy: '', creativeMapping: undefined, creativeConceptSettings: {},
};

const emptyFormForChannel = (channel) => ({
  ...EMPTY_FORM,
  goal: channel === 'universal-ads' ? 'reach' : 'performance',
  campaignSetup: setupFromBrief({}, channel),
});

const draftStorageKey = (userId, connectionId) => `adpilot-ai-campaign-draft:${userId}:${connectionId}`;
const formFromBrief = (brief) => ({
  name: brief?.campaign_name || '',
  startDate: brief?.start_date || futureDate(1),
  endDate: brief?.end_date || futureDate(30),
  budget: String(brief?.budget ?? '1000'),
  brandUrl: brief?.brand_url || '',
  geo: brief?.geo || '',
  goal: brief?.goal || 'performance',
  additionalContext: brief?.additional_context || '',
  campaignSetup: setupFromBrief(brief?.campaign_setup, brief?.channel),
  creativeMapping: brief?.creative_review?.concepts?.flatMap(concept => concept.media.map(asset => ({ ...asset, concept: concept.name }))).concat((brief?.creative_review?.excluded_media || []).map(file => ({ file, excluded: true, concept: '', placement: 'default' }))),
  creativeConceptSettings: Object.fromEntries((brief?.creative_review?.concepts || []).map(concept => [concept.name, { copy: concept.copy, audiences: concept.audiences }])),
  creativeCopy: brief?.creative_review?.copy || {}, creativeDatePolicy: brief?.creative_review?.date_policy || '',
});

const asList = (value) => (Array.isArray(value) ? value : []);
const draftInputKey = (form, creativeUpload) => JSON.stringify({
  ...form,
  budget: Number(form.budget),
  campaignSetup: { ...form.campaignSetup, budgetMarkup: Number(form.campaignSetup.budgetMarkup) },
  creativeUpload,
});
const launchPending = (launch) => ['planning', 'publishing'].includes(launch?.status) || launch?.creative_status === 'assigning';

function planCounts(template = {}) {
  // Planning and failed drafts can explicitly return a null launch template.
  template = template ?? {};
  const campaignEntries = asList(template.campaigns);
  const campaign = template.campaign || campaignEntries[0]?.campaign || campaignEntries[0] || {};
  const rootGroups = [
    ...asList(template.ad_sets),
    ...asList(template.ad_groups),
  ];
  const entryGroups = campaignEntries.flatMap((entry) => [
    ...asList(entry?.adsets),
    ...asList(entry?.ad_sets),
    ...asList(entry?.ad_groups),
  ]);
  const campaignGroups = [
    ...asList(campaign.ad_sets),
    ...asList(campaign.ad_groups),
  ];
  const groups = rootGroups.length ? rootGroups : entryGroups.length ? entryGroups : campaignGroups;
  const directAds = [
    ...asList(template.ads),
    ...asList(template.creatives),
    ...asList(campaign.ads),
    ...asList(campaign.creatives),
  ];
  const groupAds = groups.flatMap((group) => [...asList(group?.ads), ...asList(group?.creatives)]);
  const ads = directAds.length ? directAds : groupAds;
  const keywords = groups.flatMap((group) => asList(group?.keywords));
  return {
    audienceCounts: groups.reduce((counts, group) => {
      const suffix = String(group.ad_set_suffix || '').toLowerCase();
      const profiles = group.targeting_profiles || [];
      const audience = (suffix.includes('site') && suffix.includes('rtg')) || profiles.some(profile => String(profile).includes('site_rtg')) ? 'site_rtg'
        : (suffix.includes('fb') && suffix.includes('rtg')) || profiles.some(profile => String(profile).includes('fb_rtg')) ? 'fb_rtg' : 'prospecting';
      counts[audience] = (counts[audience] || 0) + 1;
      return counts;
    }, {}),
    campaigns: Math.max(1, campaignEntries.length || (Object.keys(campaign).length ? 1 : 0)),
    groups: groups.length,
    ads: ads.length,
    keywords: keywords.length,
  };
}

function ProgressStage({ done, active, children }) {
  return <li className={`${done ? 'done' : ''} ${active ? 'active' : ''}`}><span aria-hidden="true">{done ? '✓' : active ? '•' : ''}</span>{children}</li>;
}

const countedLabel = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

function AiCampaignCreatorPage({ user }) {
  const [activeChannel, setActiveChannel] = useState(readActiveChannel);
  const channel = channelFor(activeChannel);
  const performanceGoalLabel = activeChannel === 'facebook' ? 'Website traffic' : 'Drive results';
  const location = useLocation();
  const sourcesPath = location.pathname.startsWith('/demo') ? '/demo/connect' : '/connect';
  const [connection, setConnection] = useState(null);
  const [universalCreatives, setUniversalCreatives] = useState([]);
  const [pendingUniversalUpload, setPendingUniversalUpload] = useState(false);
  const [account, setAccount] = useState(null);
  const [pages, setPages] = useState([]);
  const [pageId, setPageId] = useState('');
  const [form, setForm] = useState(() => emptyFormForChannel(activeChannel));
  const effectivePageId = form.goal === 'conversion_leads' ? form.campaignSetup.metaLeadPageId : pageId;
  const [draft, setDraft] = useState(null);
  const [editingInputs, setEditingInputs] = useState(null);
  const [created, setCreated] = useState(null);
  const [creativeFile, setCreativeFile] = useState(null);
  const [creativeUpload, setCreativeUpload] = useState(null);
  const [uploadingCreative, setUploadingCreative] = useState(false);
  const [creativeInputKey, setCreativeInputKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const userId = user.id;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setConnection(null);
    setUniversalCreatives([]);
    setPendingUniversalUpload(false);
    setForm(emptyFormForChannel(activeChannel));
    setAccount(null);
    setPages([]);
    setPageId('');
    setDraft(null);
    setEditingInputs(null);
    setCreated(null);
    setCreativeFile(null);
    setCreativeUpload(null);
    setUploadingCreative(false);
    setCreativeInputKey((current) => current + 1);
    setError(null);
    const load = async () => {
      try {
        if (user.preview) {
          const demo = {
            connection_id: activeChannel === 'facebook' ? 'demo-meta' : 'demo-google',
            ad_account_id: activeChannel === 'facebook' ? 'act_123' : '1234567890',
            provider: activeChannel, access_role: 'owner',
          };
          setConnection(demo);
          setAccount({ ad_account_id: demo.ad_account_id, name: 'Demo account', currency: 'USD' });
          if (activeChannel === 'facebook') {
            setPages([{ page_id: 'page-1', name: 'Demo Page' }]);
            setPageId('page-1');
          }
          return;
        }
        const current = (await getConnection({ userId, provider: activeChannel })).connection;
        if (cancelled) return;
        setConnection(current);
        if (!current?.ad_account_id) return;
        if (activeChannel === 'facebook') {
          const [accounts, availablePages] = await Promise.all([
            listAdAccounts({ userId, connectionId: current.connection_id }),
            listFacebookPages({ userId, connectionId: current.connection_id }),
          ]);
          if (!cancelled) {
            setAccount(accounts.find((item) => item.ad_account_id === current.ad_account_id) || null);
            setPages(availablePages);
            if (availablePages.length === 1) setPageId(availablePages[0].page_id);
          }
        } else if (activeChannel === 'universal-ads') {
          const base = `/api/v3/adpilot/universal-ads/${current.connection_id}`;
          const [video, accounts] = await Promise.all([api.get(`${base}/catalog?entity=creative`), api.get('/api/v3/adpilot/universal-ads/accounts', { params: { connectionId: current.connection_id } })]);
          if (!cancelled) {
            setAccount(accounts.data.ad_accounts.find(row => row.ad_account_id === current.ad_account_id) || null);
            setUniversalCreatives(video.data.data || []);
          }
        } else if (activeChannel === 'google-ads') {
          const accounts = await listGoogleAdsCustomers({ userId, connectionId: current.connection_id });
          if (!cancelled) setAccount(accounts.find((item) => String(item.customer_id || item.ad_account_id).replace(/-/g, '') === String(current.ad_account_id).replace(/-/g, '')) || {
            customer_id: current.ad_account_id, name: `Google Ads ${current.ad_account_id}`, currency: 'USD',
          });
        }
        const storageKey = draftStorageKey(userId, current.connection_id);
        const savedDraftId = window.sessionStorage.getItem(storageKey);
        if (savedDraftId && !cancelled) {
          try {
            const restored = await getAiCampaignDraft({ userId, draftId: savedDraftId });
            if (!cancelled) {
              setDraft(restored);
              if (restored?.draft?.brief) {
                setForm(formFromBrief(restored.draft.brief));
                setCreativeUpload(restored.draft.brief.creative_upload || null);
              }
              if (restored?.launch?.status === 'published' && !launchPending(restored.launch)) setCreated(restored.published || restored);
              if (restored?.launch?.status === 'failed') setError(friendlyCampaignError(restored.launch.error, 'We couldn’t create your campaign'));
            }
          } catch (restoreError) {
            if (restoreError?.response?.status === 404) window.sessionStorage.removeItem(storageKey);
            else throw restoreError;
          }
        }
      } catch (loadError) {
        if (!cancelled && loadError?.response?.status !== 404) setError(campaignErrorMessage(loadError, 'Could not prepare campaign creation'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeChannel, user.preview, userId]);

  const pendingDraftId = draft?.launch?.id;
  const isDraftPending = launchPending(draft?.launch);
  useEffect(() => {
    if (user.preview || !pendingDraftId || !isDraftPending) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const latest = await getAiCampaignDraft({ userId, draftId: pendingDraftId });
        if (cancelled) return;
        setDraft(latest);
        if (latest.launch.status === 'published' && !launchPending(latest.launch)) {
          setError(null);
          setCreated(latest.published || latest);
        } else if (latest.launch.status === 'failed') {
          setError(friendlyCampaignError(latest.launch.error, 'We couldn’t create your campaign'));
        } else {
          setError(null);
        }
      } catch (pollError) {
        if (!cancelled) setError(campaignErrorMessage(pollError, 'Could not check campaign status'));
      }
    };
    const timer = window.setInterval(poll, 1500);
    poll();
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [pendingDraftId, isDraftPending, user.preview, userId]);

  const money = useMemo(() => new Intl.NumberFormat(undefined, {
    style: 'currency', currency: account?.currency || 'USD', maximumFractionDigits: 2,
  }), [account?.currency]);
  const days = Math.max(1, Math.round((new Date(`${form.endDate}T00:00:00Z`) - new Date(`${form.startDate}T00:00:00Z`)) / 86400000) + 1);
  const pending = launchPending(draft?.launch);
  const counts = planCounts(draft?.draft?.launch_template);
  const readiness = activeChannel === 'facebook' ? creativeReadiness(creativeUpload, draft?.draft?.brief?.creative_review, draft?.draft?.launch_template) : { blocked: false, adCount: null };
  const targetingError = activeChannel === 'facebook' ? metaTargetingReadiness(draft?.draft?.brief, draft?.draft?.launch_template) : '';
  const displayedAdCount = readiness.adCount ?? counts.ads;
  const termsAction = draft?.launch?.action_required?.type === 'accept_meta_audience_terms'
    ? draft.launch.action_required : null;
  const deliveryAction = draft?.launch?.action_required?.type === 'inspect_meta_delivery'
    ? draft.launch.action_required : null;
  const canReuseDraft = !readiness.blocked && !targetingError && editingInputs !== null && draft?.launch?.status === 'draft'
    && Boolean(draft?.draft?.launch_template) && !creativeFile
    && editingInputs === draftInputKey(form, creativeUpload);
  const update = (key) => (event) => {
    const value = event.target.value;
    const shopping = key === 'goal' && value === 'standard_shopping';
    if (shopping) { setCreativeUpload(null); setCreativeFile(null); }
    setForm((current) => ({ ...current, [key]: value,
      ...(shopping ? { geo: '', additionalContext: '', creativeCopy: {}, creativeMapping: undefined, creativeConceptSettings: {} } : {}),
      ...(key === 'goal' ? { campaignSetup: { ...current.campaignSetup, metaConversionEvent: '', trackingFileName: '', crmIntegrationReady: false,
        ...(value === 'conversion_leads' ? { fbRtg: false, siteRtg: false } : {}),
        ...(value === 'advantage_sales' ? { metaDevice: 'all', metaAudienceMode: 'broad', fbRtg: false, siteRtg: false } : {}),
        ...(shopping ? { phoneNumber: '', budgetMarkup: 1 } : {}) } } : {}),
    }));
  };
  const editDraft = () => {
    setEditingInputs(draftInputKey(form, creativeUpload));
    setError(null);
  };
  const resetDraft = () => {
    if (connection?.connection_id) {
      window.sessionStorage.removeItem(draftStorageKey(userId, connection.connection_id));
    }
    setDraft(null);
    setEditingInputs(null);
    setCreated(null);
    setError(null);
    setForm(emptyFormForChannel(activeChannel));
    setCreativeFile(null);
    setCreativeUpload(null);
    setCreativeInputKey((current) => current + 1);
  };

  const handleCreativeFile = (event) => {
    const file = event.target.files?.[0] || null;
    setCreativeUpload(null);
    if (!file) {
      setCreativeFile(null);
      return;
    }
    if (!file.name.toLowerCase().endsWith('.zip') && !(activeChannel === 'google-ads' && file.name.toLowerCase().endsWith('.xlsx'))) {
      setCreativeFile(null);
      setError(activeChannel === 'google-ads' ? 'Choose an Excel workbook (.xlsx) or ZIP package.' : 'Creative assets must be packaged in a ZIP file.');
      event.target.value = '';
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setCreativeFile(null);
      setError('Creative ZIP files must be 100 MB or smaller.');
      event.target.value = '';
      return;
    }
    setError(null);
    setCreativeFile(file);
  };

  const handleCreativeUpload = async () => {
    if (!creativeFile || !connection?.connection_id) return;
    setUploadingCreative(true);
    setError(null);
    try {
      const result = user.preview
        ? {
            id: 'demo-creative', filename: creativeFile.name, size: creativeFile.size,
            gcs_path: `adpilot/demo/creative-uploads/${connection.ad_account_id}/demo-creative/${creativeFile.name}`,
            uploaded_at: new Date().toISOString(),
          }
        : await uploadCreativeZip({ connectionId: connection.connection_id, file: creativeFile });
      setCreativeUpload(result);
      setForm(current => ({ ...current, creativeMapping: result.manifest?.media_mode === 'concepts' ? initialCreativeMapping(result.manifest) : undefined, creativeConceptSettings: {} }));
      setCreativeFile(null);
      setCreativeInputKey((current) => current + 1);
      return result;
    } catch (uploadError) {
      setError(campaignErrorMessage(uploadError, 'Could not upload creative assets. Try again or remove the file to continue.'));
      return null;
    } finally {
      setUploadingCreative(false);
    }
  };

  const startDraft = async (event) => {
    event.preventDefault();
    if (submitting || uploadingCreative || pendingUniversalUpload) return;
    if (canReuseDraft) {
      setEditingInputs(null);
      setError(null);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const uploaded = creativeFile ? await handleCreativeUpload() : creativeUpload;
      if (creativeFile && !uploaded) return;
      const missing = missingCreativeCopy(uploaded, form.creativeCopy);
      if (missing.length) {
        setError(`Your files are uploaded. Add the required ${missing.map(key => key.replace(/_/g, ' ')).join(', ')} below, then build your draft.`);
        return;
      }
      const result = user.preview
        ? { launch: { id: 'demo-draft', status: 'draft', provider: activeChannel }, draft: { brief: { ...form, start_date: form.startDate, end_date: form.endDate, budget: Number(form.budget), brand_url: form.brandUrl, additional_context: form.additionalContext, campaign_name: form.name, channel: activeChannel === 'facebook' ? 'fb' : activeChannel === 'universal-ads' ? 'universal-ads' : 'sem', ...(uploaded ? { creative_upload: uploaded } : {}) }, launch_template: { preview: true } } }
        : await draftAiCampaign({ userId, connectionId: connection.connection_id, ...form, ...(creativeFile ? { creativeMapping: undefined, creativeConceptSettings: {} } : {}), ...(uploaded ? { creativeUpload: uploaded } : {}) });
      setDraft(result);
      if (result?.draft?.brief?.creative_upload) setCreativeUpload(result.draft.brief.creative_upload);
      setEditingInputs(null);
      if (!user.preview && result?.launch?.id) {
        window.sessionStorage.setItem(draftStorageKey(userId, connection.connection_id), result.launch.id);
      }
    } catch (draftError) {
      setError(campaignErrorMessage(draftError, 'Could not prepare the campaign draft'));
    } finally {
      setSubmitting(false);
    }
  };

  const publish = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (activeChannel === 'facebook' && !effectivePageId) throw new Error('Choose the Facebook Page for this campaign');
      if (user.preview) {
        setCreated({ campaign_id: 'demo-123', name: form.name, status: 'PAUSED', watched: true, platform_url: activeChannel === 'facebook' ? 'https://adsmanager.facebook.com/' : activeChannel === 'universal-ads' ? 'https://manage.universalads.com/' : 'https://ads.google.com/' });
        return;
      }
      const result = await publishAiCampaign({ userId, draftId: draft.launch.id, pageId: effectivePageId });
      setDraft((current) => ({ ...current, ...result }));
    } catch (publishError) {
      setError(campaignErrorMessage(publishError, `Could not create the ${channel.label} campaign`));
    } finally {
      setSubmitting(false);
    }
  };


  if (activeChannel === 'chatgpt-ads') {
    return <div className="page ai-campaign-creator">
      <header className="page-header"><div><p className="eyebrow">AI campaign creator</p><h1 className="page-title">Campaign creation for ChatGPT Ads is coming.</h1><p className="page-subtitle">Choose a channel to start building your next campaign.</p></div></header>
      <ChannelTabs active={activeChannel} onSelect={setActiveChannel} />
      <CampaignStatusCard pending eyebrow="Coming soon" title="Create with ChatGPT Ads"
        description="Campaign creation is available today for Meta and Google Ads. ChatGPT Ads creation will be available here when it is supported."
        actions={<><button className="btn btn-secondary" onClick={() => setActiveChannel('google-ads')}>Create with Google Ads</button><button className="btn btn-primary" onClick={() => setActiveChannel('facebook')}>Create with Meta</button></>} />
    </div>;
  }

  const unavailable = !connection?.ad_account_id || !account;
  return (
    <div className="page campaign-builder ai-campaign-creator">
      <header className="page-header"><div><p className="eyebrow">AI campaign creator</p><h1 className="page-title">{form.goal === 'standard_shopping' ? 'Create a Standard Shopping campaign.' : channel.copy.createTitle}</h1><p className="page-subtitle">A short brief becomes a reviewable, paused campaign. Nothing goes live or spends money until you approve it in {channel.label}.</p></div>{(loading || pending || submitting) && <span className="agent-online agent-online-loading" role="status"><span className="spinner" aria-hidden="true" />{loading ? 'Loading workspace' : uploadingCreative ? 'Uploading creatives' : draft?.launch?.creative_status === 'assigning' ? 'Assigning creatives' : draft?.launch?.status === 'publishing' ? `Creating in ${channel.label}` : 'Building your plan'}</span>}</header>
      <ChannelTabs active={activeChannel} onSelect={setActiveChannel} />
      {error && !termsAction && !deliveryAction && <div className="notice notice-error" role="alert">{error}</div>}
      {loading ? <div className="ai-creator-entry" aria-busy="true" aria-label={`Loading ${channel.label} campaign creator`}><div className="connection-summary loading-summary" aria-hidden="true"><span /><span /></div><div className="campaign-compose ai-campaign-compose loading-compose" aria-hidden="true"><section className="compose-main card"><span className="loading-line loading-line-short" /><span className="loading-line" /><span className="loading-field" /><span className="loading-field" /><span className="loading-block" /></section><aside className="guardrail-card card"><span className="loading-line loading-line-short" /><span className="loading-field" /><span className="loading-field" /><span className="loading-field" /><span className="loading-button" /></aside></div></div> : unavailable ? (
        <div className="card empty-state"><div className="empty-icon">+</div><h2>{`Connect a ${channel.label} account first`}</h2><p>{`Choose the ${channel.label} account this campaign should belong to.`}</p><Link className="btn btn-primary" to={sourcesPath}>Open Sources</Link></div>
      ) : termsAction ? (
        <section className="card ai-campaign-terms" role="alert">
          <p className="eyebrow">Action required in Meta</p>
          <h2>Accept Meta’s Custom Audience Terms</h2>
          <p>{termsAction.message}</p>
          <p>Open Meta, select this ad account, and review and accept the terms there.</p>
          {termsAction.campaign_id && <p>Campaign <strong>{termsAction.campaign_id}</strong> was already created, but its retargeting setup is incomplete. Review and complete the existing campaign in Meta before starting another one.</p>}
          <p>Opening the terms page does not automatically accept them or retry campaign creation.</p>
          <div className="form-actions">
            <a className="btn btn-primary" href={termsAction.url} target="_blank" rel="noopener noreferrer">Accept terms in Meta ↗</a>
            {termsAction.campaign_url && <a className="btn btn-secondary" href={termsAction.campaign_url} target="_blank" rel="noopener noreferrer">Open existing campaign ↗</a>}
          </div>
        </section>
      ) : deliveryAction ? (
        <section className="card ai-campaign-terms" role="alert">
          <p className="eyebrow">Campaign created · verification incomplete</p>
          <h2>Review delivery settings in Meta</h2>
          <p>{deliveryAction.message}</p>
          <p>Campaign <strong>{deliveryAction.campaign_id}</strong></p>
          {deliveryAction.ad_set_ids?.length > 0 && <p>Created ad sets: {deliveryAction.ad_set_ids.join(', ')}</p>}
          <p>Check the objective, optimization, destination and paused status. AdPilot has stopped before assigning your creative package.</p>
          <a className="btn btn-primary" href={deliveryAction.campaign_url} target="_blank" rel="noopener noreferrer">Open existing campaign ↗</a>
        </section>
      ) : draft?.launch?.status === 'failed' && draft?.draft?.brief?.goal === 'standard_shopping' && draft?.launch?.shopping_result?.creation_dispatched ? (
        <section className="card" role="alert"><h2>Inspect Shopping creation in Google Ads</h2>
          <p>Creation could not be fully confirmed. Review the existing account before creating another campaign.</p>
          {draft.launch.shopping_result?.campaign_id && <p>Campaign: {draft.launch.shopping_result.campaign_id}</p>}
          {draft.launch.shopping_result?.resources && <details><summary>Saved campaign and ad resources</summary><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(draft.launch.shopping_result.resources, null, 2)}</pre></details>}
          <a className="btn btn-primary" href="https://ads.google.com/" target="_blank" rel="noopener noreferrer">Open Google Ads ↗</a>
        </section>
      ) : pending ? (
        <section className="card ai-campaign-progress" aria-busy="true">
          <div className="ai-progress-heading"><span className="ai-progress-spinner spinner" aria-hidden="true" /><div><p className="eyebrow">AdPilot is working</p><h2>{draft.launch.creative_status === 'assigning' ? (draft.launch.creative_action === 'reconcile' ? 'Checking your existing ads' : 'Assigning your creative package') : draft.launch.status === 'publishing' ? `Creating your paused ${channel.label} campaign` : 'Building your campaign plan'}</h2><p>{draft.launch.creative_status === 'assigning' ? 'Your campaign has been created and remains paused while its ads are prepared.' : draft.launch.status === 'publishing' ? `This usually takes a few minutes while ${channel.label} creates and verifies the campaign.` : 'AdPilot is turning your brief into targeting, structure, and setup details for your review.'}</p></div></div>
          <ol className="ai-progress-steps" style={{ '--progress-step-count': creativeUpload ? 4 : 3 }}>
            <ProgressStage done>Campaign details</ProgressStage>
            <ProgressStage done={['publishing', 'published'].includes(draft.launch.status)} active={draft.launch.status === 'planning'}>Reviewable plan</ProgressStage>
            <ProgressStage done={draft.launch.status === 'published'} active={draft.launch.status === 'publishing'}>Paused campaign</ProgressStage>
            {creativeUpload && <ProgressStage active={draft.launch.creative_status === 'assigning'}>Creative assignment</ProgressStage>}
          </ol>
          <p className="ai-progress-note">You can safely leave this page and come back. Your progress is saved automatically.</p>
        </section>
      ) : created ? (
        <CampaignStatusCard eyebrow={draft?.launch?.creative_status === 'failed' ? 'Campaign created · ads incomplete' : 'Campaign created'} title={created.name || form.name}
          description={`Your campaign is saved in ${channel.label}. Review your ads there before activating it.`}
          statuses={<><span className="status-pill">Paused · no spend</span>{created.watched && <span className="status-pill monitoring-on">Monitoring on</span>}</>}
          actions={<><button type="button" className="btn btn-secondary" onClick={resetDraft}>Create another campaign</button><a className="btn btn-primary" href={created.platform_url} target="_blank" rel="noopener noreferrer">Open {channel.label} ↗</a></>}>
          {form.goal === 'standard_shopping' && <p>One Shopping product ad verified. View its performance using Imported campaigns. Automatic Shopping optimization is not supported yet.</p>}
          {activeChannel === 'universal-ads' && (created.warnings || []).length > 0 && <aside className="notice notice-warning" aria-label="Comcast setup warnings"><h3>Review before activation</h3>{created.warnings.map((warning, index) => <p key={index}>{typeof warning === 'string' ? warning : warning.message || JSON.stringify(warning)}</p>)}</aside>}
          {isAdpilotWorkflow(draft?.draft?.brief?.campaign_setup?.workflow) && <>
            {creativeUpload && <p className={`campaign-status-note${draft?.launch?.creative_status === 'failed' ? ' needs-attention' : ''}`}>
              {draft?.launch?.creative_status === 'failed'
                ? `Your campaign was created, but some ads still need attention. Complete their setup in ${channel.label} before activating it.`
                : draft?.launch?.creative_status === 'assigned'
                  ? 'Your creative package has been added. Review the final ads before activating your campaign.'
                  : 'Your campaign is paused. Check that your creative package is ready before activating it.'}
            </p>}
            <CreativeAssignmentResults report={draft?.launch?.creative_assignment} />
            <CreativeRecovery key={draft?.launch?.id} launch={draft?.launch} onChanged={result => setDraft(current => ({ ...current, launch: result.launch }))} />
            <details className="campaign-status-details"><summary>Review setup and tracking</summary>
              <CampaignSetupReview brief={draft?.draft?.brief} currency={account?.currency || 'USD'} template={draft?.draft?.launch_template} creativeUpload={creativeUpload} launch={draft?.launch} retargeting={draft?.retargeting} />
            </details>
          </>}
        </CampaignStatusCard>
      ) : draft?.launch?.status === 'draft' && editingInputs === null ? (
        <div className="ai-campaign-review card"><div className="ai-review-heading"><div><p className="eyebrow">Ready for review</p><h2>{form.name}</h2><p>Confirm the essentials below. AdPilot will create this as paused so you can inspect everything in {channel.label} before it can spend.</p></div><span className="status-pill">Draft · no spend</span></div><div className="ai-review-grid"><div><span>Platform</span><strong>{channel.label}</strong></div><div><span>Goal</span><strong>{({ performance: performanceGoalLabel, reach: 'Build reach', advantage_sales: 'Advantage+ sales', website_sales: 'Website sales', website_leads: 'Website leads', conversion_leads: 'Conversion leads', standard_shopping: 'Standard Shopping' })[form.goal]}</strong></div><div><span>Flight</span><strong>{form.startDate} → {form.endDate}</strong></div><div><span>{form.goal === 'standard_shopping' ? 'Planned flight budget' : 'Total budget'}</span><strong>{money.format(Number(form.budget))}</strong></div><div><span>Geography</span><strong>{draft?.draft?.launch_template?.shopping_summary?.country_name || draft?.draft?.launch_template?.ai_campaign?.brief?.geo || form.geo || (activeChannel === 'facebook' ? 'United States (default)' : 'Platform default')}</strong></div><div><span>{form.goal === 'standard_shopping' ? 'Brand website · ad URLs come from the feed' : form.goal === 'conversion_leads' ? 'Brand website · ads open the Instant Form' : 'Landing page'}</span><strong className="ai-review-url">{form.brandUrl}</strong></div></div>{creativeUpload && <section className="ai-review-creative"><span className="ai-review-creative-icon">✓</span><div><span>Creative package</span><strong>{creativeUpload.filename}</strong><p>Stored privately with this draft. Confirm the final ads in {channel.label} before activation.</p></div></section>}<CreativePackageReview upload={creativeUpload} review={draft?.draft?.brief?.creative_review} value={form} audienceCounts={counts.audienceCounts} /><section className="ai-plan-summary"><h3>What AdPilot will create</h3>{(readiness.blocked || targetingError) && <p className="notice notice-warning" role="alert">{readiness.message || targetingError}</p>}<div className="ai-plan-counts"><span>{countedLabel(counts.campaigns, 'campaign')}</span>{counts.groups > 0 && <span>{countedLabel(counts.groups, activeChannel === 'google-ads' ? 'ad group' : 'ad set')}</span>}{!readiness.blocked && <span>{creativeUpload && readiness.adCount === null && counts.ads === 0 ? 'Ads assigned after setup' : `${countedLabel(displayedAdCount, 'ad')}${readiness.adCount !== null ? ' planned' : ''}`}</span>}{counts.keywords > 0 && <span>{countedLabel(counts.keywords, 'keyword')}</span>}</div><ul>{creativeUpload && <li>{readiness.blocked ? readiness.message : readiness.adCount !== null ? `${readiness.adCount} ads will be created from the approved creative package after campaign setup.` : 'Ads are assigned from your package after campaign setup; the final count depends on its template.'}</li>}<li>Starts paused in {channel.label}</li><li>{form.goal === 'standard_shopping' ? 'Available in Performance under Imported campaigns; automatic Shopping optimization is not supported yet' : 'Added to AdPilot monitoring when capacity is available'}</li><li>Requires a separate activation before delivery begins</li></ul></section>{activeChannel === 'facebook' && form.goal !== 'conversion_leads' && <label className="builder-field ai-page-choice"><span>Facebook Page</span><select value={pageId} onChange={(event) => setPageId(event.target.value)}><option value="">Choose a Page</option>{pages.map((page) => <option key={page.page_id} value={page.page_id}>{page.name}</option>)}</select><small>The ads will appear from this Page.</small></label>}{isAdpilotWorkflow(draft.draft.brief?.campaign_setup?.workflow) && <CampaignSetupReview brief={draft?.draft?.brief} currency={account?.currency || 'USD'} template={draft.draft.launch_template} creativeUpload={creativeUpload} />}<div className="form-actions"><button type="button" className="btn btn-secondary" disabled={submitting} onClick={editDraft}>Edit inputs</button><button type="button" className="btn btn-primary" disabled={submitting || readiness.blocked || Boolean(targetingError) || (activeChannel === 'facebook' && !effectivePageId)} onClick={publish}>{submitting ? `Creating in ${channel.label}…` : 'Approve & create paused'}</button></div></div>
      ) : (
        <div className="ai-creator-entry">
          <div className="connection-summary">
            <span className="summary-pill">{account.name || channel.label} · {account.currency || 'USD'}</span>
            <span className="summary-pill">{connection.ad_account_id}</span>
            <Link className="summary-action" to={sourcesPath}>Change account</Link>
          </div>
          <form className="campaign-compose ai-campaign-compose" onSubmit={startDraft}>
            <section className="compose-main card">
              <div className="compose-heading"><span className="agent-avatar">A</span><div><p className="eyebrow">Campaign brief</p><h2>Tell us what you want to promote</h2><p>Give AdPilot the essentials. You will review the complete plan before anything is created.</p></div></div>
              <label className="builder-field"><span>Campaign name</span><input required value={form.name} onChange={update('name')} placeholder="Fall service leads" /></label>
              <label className="builder-field"><span>Brand or landing-page URL</span><input required type="url" value={form.brandUrl} onChange={update('brandUrl')} placeholder="https://example.com" /></label>
              <CampaignSetupFields pages={pages} goal={form.goal} connectionId={connection?.connection_id} preview={user.preview} provider={activeChannel} value={form.campaignSetup} onChange={(campaignSetup) => setForm((current) => ({ ...current, campaignSetup }))} />
              {form.goal !== 'standard_shopping' && (activeChannel === 'universal-ads' ? <UniversalAdsCreativeAssets key={connection.connection_id}
                connectionId={connection.connection_id} creatives={universalCreatives} selectedIds={form.campaignSetup.universalCreativeIds || []}
                optional preview={user.preview} disabled={submitting} onPendingChange={setPendingUniversalUpload} onBusyChange={setUploadingCreative}
                onSelectionChange={(universalCreativeIds) => setForm((current) => ({ ...current, campaignSetup: { ...current.campaignSetup, universalCreativeIds } }))}
                onUploaded={(creative) => {
                  setUniversalCreatives((current) => [...current.filter((row) => row.id !== creative.id), creative]);
                  setForm((current) => ({ ...current, campaignSetup: { ...current.campaignSetup,
                    universalCreativeIds: [...new Set([...(current.campaignSetup.universalCreativeIds || []), creative.id])] } }));
                }} /> : <div className="creative-upload">
                <div><strong>Creative assets <span>Optional</span></strong><p>{activeChannel === 'facebook' ? 'Upload a ZIP with images or video and the creative copy/instructions used by the Facebook assignment workflow.' : 'Upload a Search Excel workbook (.xlsx), or a ZIP containing one Search creative JSON or Excel specification. It will be assigned through the shared Search workflow.'} AdPilot assigns the package after you approve creation.</p></div>
                {activeChannel === 'facebook' && <details className="creative-package-help">
                  <summary>What should I include in the ZIP?</summary>
                  <p>Include at least one JPG, PNG, WebP, GIF, MP4, or MOV. Images and videos can be mixed, and folders are supported. AdPilot suggests concepts from filenames. Placement variants of the same concept can share one ad. Review the grouping, copy, and audiences before approval.</p>
                  <p>Optionally add a text file with the fields below, or fill them in here after uploading. All four are required before drafting. Your campaign landing-page URL and selected Facebook Page will be used for the ads.</p>
                  <pre>{'PRIMARY_TEXT: Your main ad message\nHEADLINE: A short headline\nDESCRIPTION: Supporting details\nCALL_TO_ACTION: SHOP_NOW'}</pre>
                  <p>Use Card 1.jpg, Card 2.jpg, and so on for an image carousel. Existing spreadsheet and placement packages are also supported.</p>
                </details>}
                <label className="creative-file-picker"><input key={creativeInputKey} aria-label="Creative ZIP file" type="file" disabled={uploadingCreative || submitting} accept={activeChannel === 'google-ads' ? '.xlsx,.zip' : '.zip,application/zip'} onChange={handleCreativeFile} /><span>{creativeFile?.name || (activeChannel === 'google-ads' ? 'Choose Excel or ZIP file' : 'Choose ZIP file')}</span></label>
                {creativeFile && <button type="button" className="btn btn-secondary" onClick={handleCreativeUpload} disabled={uploadingCreative || submitting}>{uploadingCreative ? <><span className="spinner" />Uploading…</> : 'Upload creatives'}</button>}
                {creativeFile && <div className="creative-pending" role="status">
                  <p>{uploadingCreative ? 'Uploading your creative file…' : 'Ready to upload. Build campaign draft will upload this file automatically.'}</p>
                  <button type="button" className="text-button" disabled={uploadingCreative || submitting} onClick={() => { setCreativeFile(null); setCreativeInputKey((current) => current + 1); setError(null); }}>Remove selected file</button>
                </div>}
                {creativeUpload && <div className="creative-uploaded" role="status"><span>✓</span><div><strong>{creativeUpload.filename}</strong><small>Saved with this draft for {account.name || channel.label}</small></div><button type="button" className="text-button" disabled={uploadingCreative || submitting} onClick={() => setCreativeUpload(null)}>Remove</button></div>}
              </div>)}
              {activeChannel === 'google-ads' && form.goal !== 'standard_shopping' && <p>Search workbook: pair KW - Group and ETA - Group sheets. Use Headline and Description columns for one responsive ad, or add Ad ID and optional Final URL columns to identify 1–3 distinct ads per group.</p>}
              <CreativePackageReview upload={creativeUpload} value={form} onChange={setForm} />
              {form.goal !== 'standard_shopping' && <div className="builder-field ai-context-field"><label htmlFor="additional-context">Additional context <small>Optional</small></label><textarea id="additional-context" aria-describedby="additional-context-help" value={form.additionalContext} onChange={update('additionalContext')} placeholder="For example: Target US only, iPhone users. No other targeting to start." /><small id="additional-context-help">Include targeting restrictions here along with creative guidance. AdPilot will resolve supported instructions and show the targeting for review before approval.</small></div>}
            </section>
            <aside className="guardrail-card card">
              <p className="eyebrow">Your guardrails</p>
              <h2>You stay in control.</h2>
              <div className="builder-field-row"><label className="builder-field"><span>Start date</span><input required type="date" value={form.startDate} onChange={update('startDate')} /></label><label className="builder-field"><span>End date</span><input required type="date" min={form.startDate} value={form.endDate} onChange={update('endDate')} /></label></div>
              <label className="builder-field"><span>{form.goal === 'standard_shopping' ? 'Planned flight budget' : 'Total budget'} · {account.currency || 'USD'}</span><input required type="number" min="1" step="0.01" value={form.budget} onChange={update('budget')} /></label>
              <label className="builder-field"><span>Goal</span><select value={form.goal} onChange={update('goal')}><option value="performance">{performanceGoalLabel}</option><option value="reach">Build reach</option>{activeChannel === 'facebook' && <><option value="advantage_sales">Advantage+ sales</option><option value="website_sales">Website sales</option><option value="website_leads">Website leads</option><option value="conversion_leads">Conversion leads · Instant Form + CRM</option></>}{activeChannel === 'google-ads' && <option value="standard_shopping">Standard Shopping</option>}</select></label>
              {activeChannel === 'facebook' && form.goal === 'performance' && <p>Optimizes for link clicks to your website.</p>}
              {form.goal !== 'standard_shopping' && <label className="builder-field"><span>Geography <small>Optional</small></span><input value={form.geo} onChange={update('geo')} placeholder={activeChannel === 'universal-ads' ? 'United States nationwide, or 90026, 90027' : 'Los Angeles, CA or 90026'} /></label>}
              {form.goal === 'standard_shopping' && <p>The average daily budget is derived from this amount and flight. Google Ads may vary daily spend; this is not a guaranteed total cap.</p>}
              <div className="ai-budget-note"><strong>{days} days</strong><span>Approximately {money.format(Number(form.budget || 0) / days)} per day</span></div>
              <button className="btn btn-primary builder-submit" type="submit" disabled={submitting || uploadingCreative || pendingUniversalUpload}>{uploadingCreative ? 'Uploading creatives…' : submitting ? 'Building draft…' : canReuseDraft ? 'Return to review →' : 'Build campaign draft →'}</button>
              <small className="safe-note">Draft only. Nothing goes live or spends money without your approval.</small>
            </aside>
          </form>
        </div>
      )}
    </div>
  );
}

export default AiCampaignCreatorPage;
